import { ProjectTabs } from "@/components/nav/project-tabs";
import { AutonomyBadge } from "@/components/projects/autonomy-badge";
import { ProjectDot } from "@/components/projects/project-color";
import {
  ProjectServiceButtons,
  type ProjectServiceLinks,
} from "@/components/projects/project-service-buttons";
import { ProjectVision } from "@/components/projects/project-vision";
import { AppLink } from "@/components/ui/app-link";
import { ApiError, api } from "@/lib/api";
import { Outlet, redirect, useLoaderData } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

/**
 * Project layout shell + its overview loader. Lives outside the route
 * file so the GitLab-native `/{group}/{project}` route can reuse both
 * without importing from a route file — that would defeat the router
 * plugin's code-splitting.
 */

export type Person = { id: string; name: string | null };

export type ProjectOverview = {
  project: {
    id: string;
    name: string;
    slug: string;
    gitlabProjectId: string;
    createdAt: string;
    useReleases: boolean;
    autonomyLevel?: number;
    /** Nullable since the 2026-05-20 Customer ↔ GitLab decouple —
     *  a freshly imported project has no customer until admin /
     *  accountant assigns one. */
    customer: { id: string; name: string } | null;
    workItems: Array<{
      id: string;
      title: string;
      gitlabIssueId: string;
      globalId: number;
      priority: string;
      status: { name: string; color: string } | null;
    }>;
  };
  timeEntries: Array<{
    id: string;
    hours: string;
    spentOn: string;
    /** Null for external Clockify imports (owner has no Hub user). */
    user: Person | null;
    externalUserEmail: string | null;
    workItem: { title: string } | null;
  }>;
  canSeeMeta: boolean;
  /** Viewer may edit the project — manager or Hub admin. */
  canEdit: boolean;
  serviceLinks: ProjectServiceLinks;
  /** Viewer reaches the project's Seafile folder, at least its team folders. */
  hasFiles?: boolean;
  hasKeys?: boolean;
  stats: {
    statusPie: Array<{ label: string; value: number; color?: string }>;
    priorityBar: Array<{ label: string; value: number; color?: string }>;
    userHoursBar: Array<{ label: string; value: number }>;
    velocity: Array<{ label: string; value: number }>;
  };
};

/**
 * Fetch a project's overview payload by slug. Shared by this
 * layout and the GitLab-native `/{group}/{project}` layout so both
 * URL shapes resolve a project the same way.
 */
// Same rule as the app layout's `lastGoodLayout`: live-refresh re-runs
// this loader on every socket event, and one blip must not replace the
// whole project shell with the router's bare error box mid-task.
const lastGoodOverview = new Map<string, ProjectOverview>();

export async function loadProjectOverview(slug: string): Promise<ProjectOverview> {
  try {
    const data = await api.get<ProjectOverview>(
      `/api/projects/${encodeURIComponent(slug)}/overview`,
    );
    lastGoodOverview.set(slug, data);
    return data;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      lastGoodOverview.delete(slug);
      throw redirect({ to: "/projects" });
    }
    const cached = lastGoodOverview.get(slug);
    if (cached) return cached;
    throw err;
  }
}
export function ProjectLayout({ data }: { data: ProjectOverview }) {
  const { project } = data;
  const { t } = useTranslation();
  const layout = useLoaderData({ from: "/_app" });
  const isCustomer = layout.isCustomer;
  // Customer is a billing concept: accountants and admins only. The server
  // already nulls it out for everyone else — this mirrors the rule so a
  // stale cached payload can't render a link the viewer would only get a
  // 403 from. Same gate as `ProjectHeader`.
  const canSeeCustomer =
    layout.user.globalRole === "admin" || layout.user.globalRole === "accountant";
  return (
    <div>
      <div className="mb-1 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">
            {t("projectHeader.project")}
          </p>
          <h1 className="font-heading text-2xl font-bold leading-tight">
            <ProjectDot projectId={project.id} className="mr-2 h-3 w-3 align-middle" />
            {project.name}
            {!isCustomer && project.autonomyLevel !== undefined && (
              <AutonomyBadge
                level={project.autonomyLevel}
                href={data.canEdit ? `/projects/${project.slug}/settings` : undefined}
                className="ml-2"
              />
            )}
          </h1>
          {canSeeCustomer && project.customer && !isCustomer && (
            <AppLink
              href={`/customers/${project.customer.id}`}
              className="text-sm text-muted-foreground hover:text-foreground hover:underline"
            >
              {project.customer.name}
            </AppLink>
          )}
          <ProjectVision projectId={project.id} />
        </div>
        {!isCustomer && <ProjectServiceButtons links={data.serviceLinks} />}
      </div>
      <ProjectTabs
        slug={project.slug}
        canEdit={data.canEdit}
        useReleases={project.useReleases}
        isCustomer={isCustomer}
        hasFiles={data.hasFiles === true}
        hasKeys={data.hasKeys === true}
      />
      <Outlet />
    </div>
  );
}
